import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  getCoreRowModel,
  useReactTable,
  type PaginationState,
} from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/data-table/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserReport, useUserReports } from "@/lib/api/user-reports";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { createUserReportColumns } from "./columns";
import { ReportDrawer } from "./report-drawer";
import { FILTER_INPUT_CLASS, FILTER_TRIGGER_CLASS } from "../constants";
import {
  type UserReport,
  type UserReportDirection,
  type UserReportStatus,
} from "./types";

const DIRECTION_OPTIONS: {
  value: UserReportDirection | "ALL";
  label: string;
}[] = [
  { value: "ALL", label: "All directions" },
  { value: "TEEN_INITIATED", label: "Teen-initiated" },
  { value: "MENTOR_INITIATED", label: "Mentor-initiated" },
];

export function UserReportsView({
  deepLinkedReportId,
}: {
  deepLinkedReportId?: string;
}) {
  const navigate = useNavigate();
  const { data: deepLinkedReport } = useUserReport(deepLinkedReportId);
  const [activeTab, setActiveTab] = useState<UserReportStatus>("OPEN");
  const [directionFilter, setDirectionFilter] = useState<
    UserReportDirection | "ALL"
  >("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedReport, setSelectedReport] = useState<UserReport | null>(null);

  const { data, isPending } = useUserReports({
    status: activeTab,
    page,
    limit: pageSize,
    direction: directionFilter === "ALL" ? undefined : directionFilter,
    search: debouncedSearchQuery.trim() || undefined,
    dateFrom: dateFrom ? dateFrom + "T00:00:00.000Z" : undefined,
    dateTo: dateTo ? dateTo + "T23:59:59.999Z" : undefined,
  });

  const rows = data?.reports ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: page - 1, pageSize }),
    [page, pageSize],
  );

  const columns = useMemo(() => createUserReportColumns(), []);

  const table = useReactTable<UserReport>({
    data: rows,
    columns,
    pageCount,
    state: { pagination },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(pagination) : updater;
      setPage(next.pageIndex + 1);
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize);
        setPage(1);
      }
    },
  });

  const resetPage = () => setPage(1);

  return (
    <>
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v as UserReportStatus);
          resetPage();
        }}
        className="flex items-center"
      >
        <TabsList className="rounded-[10px] border border-zinc-200 bg-[#F5F4F2] p-1">
          <TabsTrigger
            value="OPEN"
            className="rounded-[8px] px-5 py-1.5 text-zinc-500 data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none"
          >
            Open
          </TabsTrigger>
          <TabsTrigger
            value="CLOSED"
            className="rounded-[8px] px-5 py-1.5 text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none"
          >
            Closed
          </TabsTrigger>
        </TabsList>

        <div className="ml-auto flex items-center gap-3">
          <Select
            value={directionFilter}
            onValueChange={(v) => {
              setDirectionFilter(v as UserReportDirection | "ALL");
              resetPage();
            }}
          >
            <SelectTrigger className={`${FILTER_TRIGGER_CLASS} w-[180px]`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIRECTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => {
              setDateFrom(e.target.value);
              resetPage();
            }}
            className={FILTER_INPUT_CLASS}
          />
          <span className="text-sm text-[#a09a93]">—</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => {
              setDateTo(e.target.value);
              resetPage();
            }}
            className={FILTER_INPUT_CLASS}
          />

          <div className="relative w-[240px]">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#a09a93]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                resetPage();
              }}
              placeholder="Search reporter, reported, category..."
              className="w-full rounded-[10px] border border-[#ece8e1] bg-white py-1.5 pl-8 pr-3 text-sm text-[#1f1e1d] placeholder:text-[#a09a93] focus:outline-none focus:ring-1 focus:ring-[#ece8e1]"
            />
          </div>
        </div>
      </Tabs>

      <DataTable
        table={table}
        isPending={isPending}
        onRowClick={(row) => setSelectedReport(row)}
        getCanNextPage={() => page < pageCount}
        getCanPreviousPage={() => page > 1}
        onPageChange={(idx) => setPage(idx + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        className="mt-4"
      />

      {(selectedReport ?? deepLinkedReport) && (
        <ReportDrawer
          report={(selectedReport ?? deepLinkedReport)!}
          onClose={() => {
            setSelectedReport(null);
            if (deepLinkedReportId) {
              navigate({
                to: "/risk-flagging",
                search: { view: "reports" },
                replace: true,
              });
            }
          }}
        />
      )}
    </>
  );
}
